// ComunicacionPlan — /admin/comunicacion/plan (26-jul-2026)
// Pasos 1 y 2 del flujo: el Estratega propone y VOS aprobás el plan semanal.
// Nace del quilombo del 26-jul: Nico aprobó el plan creyendo que aprobaba piezas
// (no vio texto ni imagen porque el plan NO las tiene) y después no encontró nada.
// Esta pantalla dice clarísimo QUÉ se aprueba acá y qué viene después.
// Absorbe la vista del calendario editorial (/admin/calendario-marketing queda
// como taller avanzado para cargar/editar piezas a mano — link abajo).

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { COM_CSS } from '../../components/comunicacion/ComunicacionUI'
import { FlujoStepper, CasoActualCard, BotonAgente, cargarEstadoFlujo, FLUJO_CSS } from '../../components/comunicacion/FlujoRedes'

const CANAL_MINI = {
  instagram_post: '📷 Post', instagram_story: '⚡ Story', instagram_reel: '🎬 Reel',
  whatsapp_dm: '💬 WA', whatsapp_grupo: '👥 WA', meta_ads: '💰 Ads', email: '📧 Email',
  volante_fisico: '📄 Volante', visita_edificio: '🏢 Visita', prensa: '📰 Prensa', otro: '🔖 Otro',
}

// Pills de estado que ENSEÑAN qué significa cada uno (no jerga de BD)
const ESTADO_FILA = {
  propuesto:  { txt: '🧠 Propuesta — falta que apruebes el plan', bg: '#EDE9FE', color: '#6B21A8' },
  pendiente:  { txt: '⏳ Esperando al Gerente (la escribe él)',    bg: '#FEF3C7', color: '#92400E' },
  en_proceso: { txt: '✍️ Escrita — revisala en Piezas',            bg: '#DBEAFE', color: '#1E40AF' },
  publicado:  { txt: '✅ Publicada',                                bg: '#D1FAE5', color: '#065F46' },
  saltado:    { txt: '⏭ Saltada',                                  bg: '#F1F5F9', color: '#64748B' },
  cancelado:  { txt: '✖ Cancelada',                                bg: '#FEE2E2', color: '#991B1B' },
}

const fmtDia = (d) => new Date(d + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'short' })

export default function ComunicacionPlan() {
  const [loading, setLoading] = useState(true)
  const [est, setEst] = useState(null)          // resultado de cargarEstadoFlujo
  const [calendario, setCalendario] = useState([])  // calendario completo (14 días para atrás/adelante)
  const [trabajando, setTrabajando] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    try {
      const estado = await cargarEstadoFlujo()
      setEst(estado)

      // Calendario completo: últimos 7 días + lo que viene (incluye filas manuales
      // y de planes anteriores — antes esto vivía suelto en /admin/calendario-marketing)
      const desde = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10)
      const { data: cal } = await supabase
        .from('calendario_marketing')
        .select('id, fecha, hora_sugerida, canal, titulo, copy, estado, origen, plan_id')
        .gte('fecha', desde)
        .order('fecha').order('hora_sugerida', { nullsFirst: false })
        .limit(80)
      setCalendario(cal || [])
    } catch (e) {
      console.error('ComunicacionPlan cargar:', e)
    } finally {
      setLoading(false)
    }
  }

  const plan = est?.plan
  const planPropuesto = plan && plan.estado === 'propuesto' ? plan : null
  const filasPlan = est?.filas || []

  async function aprobarSemana() {
    if (!planPropuesto) return
    const n = filasPlan.filter(f => f.estado === 'propuesto').length
    if (!confirm(`¿Aprobar el plan de la semana del ${planPropuesto.semana_inicio}? (${n} temas)\n\nRecordá: aprobás QUÉ se publica y cuándo — todavía no hay texto ni imagen. El Gerente escribe cada pieza después y la revisás una por una antes de que salga.`)) return
    setTrabajando(true)
    try {
      const { data, error } = await supabase.rpc('aprobar_plan_semanal', { p_plan_id: planPropuesto.id })
      if (error || data?.error) throw new Error(error?.message || data?.error)
      setMsg({ tipo: 'ok', texto: `✓ Plan aprobado: ${data?.piezas_activadas ?? '?'} temas pasan al Gerente. Siguiente paso: él escribe las piezas (corre todos los días) y te aparecen en "2 · Publicar" para tu OK.` })
      await cargar()
    } catch (e) { setMsg({ tipo: 'err', texto: '⚠ ' + e.message }) } finally { setTrabajando(false) }
  }

  async function descartarPlan() {
    if (!planPropuesto) return
    if (!confirm('¿Descartar el plan completo? Se borran los temas propuestos y podés pedirle otro al Estratega.')) return
    setTrabajando(true)
    try {
      const { data, error } = await supabase.rpc('descartar_plan_semanal', { p_plan_id: planPropuesto.id })
      if (error || data?.error) throw new Error(error?.message || data?.error)
      setMsg({ tipo: 'ok', texto: 'Plan descartado. Pedile otro al Estratega cuando quieras.' })
      await cargar()
    } catch (e) { setMsg({ tipo: 'err', texto: '⚠ ' + e.message }) } finally { setTrabajando(false) }
  }

  // Agrupar calendario por día
  const porDia = useMemo(() => {
    const map = new Map()
    calendario.forEach(i => {
      if (!map.has(i.fecha)) map.set(i.fecha, [])
      map.get(i.fecha).push(i)
    })
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [calendario])

  const hoyISO = new Date().toISOString().slice(0, 10)

  return (
    <AdminLayout title="1 · Plan semanal">
      <div className="com-wrap">

        {/* QUÉ ES ESTA PANTALLA */}
        <div className="com-hero">
          <div className="com-hero-avatar">🗓️</div>
          <div className="com-hero-body">
            <div className="com-hero-eyebrow">COMUNICACIÓN · PASOS 1 Y 2 DE 5</div>
            <h1 className="com-hero-nombre">Plan semanal</h1>
            <div className="com-hero-rol">Acá se decide QUÉ se publica y CUÁNDO — el texto y la imagen vienen después</div>
            <p className="com-hero-desc">
              El Estratega propone los temas de la semana (corre solo los domingos 20:00). Vos los aprobás acá.
              Recién después el Gerente escribe cada pieza, y esa sí la revisás con texto e imagen en "2 · Publicar".
            </p>
          </div>
          <Link to="/admin/comunicacion" className="com-hero-back">← Panel</Link>
        </div>

        <FlujoStepper pasoActual={est?.paso || 1} />

        {msg && <div className={`com-msg ${msg.tipo === 'ok' ? 'com-msg-ok' : 'com-msg-err'}`}>{msg.texto}</div>}

        {loading && !est && <div className="com-card com-empty">Cargando…</div>}

        {est && <CasoActualCard est={est} onMsg={setMsg} onDone={cargar} paginaActual="plan" />}

        {/* PLAN PROPUESTO → APROBACIÓN */}
        <section className="com-card" style={{ borderLeft: '4px solid #C5A059' }}>
          <div className="com-card-head">
            <h2 className="com-card-title">🧠 Plan propuesto por el Estratega</h2>
            {!planPropuesto && !loading && (
              <BotonAgente agente="estratega" onMsg={setMsg} onDone={cargar} />
            )}
          </div>

          {loading && <div className="com-empty">Cargando…</div>}

          {!loading && !planPropuesto && (
            <div className="com-empty">
              <strong>{plan?.estado === 'aprobado' ? `El plan de la semana del ${plan.semana_inicio} ya está aprobado ✓` : 'No hay plan esperando tu aprobación.'}</strong>
              {plan?.estado === 'aprobado'
                ? 'Abajo ves cómo viene cada tema. El próximo plan lo propone el Estratega el domingo 20:00 (o pedíselo antes con el botón de arriba).'
                : 'El Estratega corre solo los domingos 20:00 y deja acá su propuesta. Con el botón de arriba se lo pedís ahora mismo.'}
            </div>
          )}

          {!loading && planPropuesto && (
            <>
              {/* EL AVISO QUE FALTABA: qué estás aprobando exactamente */}
              <div className="fr-aviso" style={{ marginBottom: 12 }}>
                <strong>⚠️ Qué estás aprobando acá:</strong> los <strong>temas y horarios</strong> de la semana
                — QUÉ se va a publicar, no las piezas. <strong>Todavía no hay texto ni imagen:</strong> eso
                lo escribe el Gerente después (paso 3) y vas a revisar cada pieza terminada, con su foto y su
                texto, una por una en "2 · Publicar". Limpack <strong>no publica solo</strong>: ahí bajás la
                foto, copiás el texto y lo subís vos desde Meta Business Suite.
              </div>

              <div className="cplan-resumen">
                <div className="cplan-resumen-eyebrow">SEMANA DEL {planPropuesto.semana_inicio} · POR QUÉ ESTE PLAN ({planPropuesto.modelo || 'IA'})</div>
                {planPropuesto.resumen}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                {filasPlan.map(f => (
                  <div key={f.id} className="cplan-fila">
                    <span className="cplan-fila-fecha">{fmtF(f.fecha)}</span>
                    <span className="cplan-fila-hora">{(f.hora_sugerida || '').slice(0, 5)}</span>
                    <span className="cplan-fila-canal">{CANAL_MINI[f.canal] || f.canal}</span>
                    <span className="cplan-fila-titulo" title={f.copy || ''}>{f.titulo}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={aprobarSemana} disabled={trabajando} className="com-btn com-btn-green">
                  {trabajando ? '…' : `✓ Aprobar el plan (${filasPlan.filter(f => f.estado === 'propuesto').length} temas — sin texto todavía)`}
                </button>
                <button onClick={descartarPlan} disabled={trabajando} className="com-btn com-btn-danger">✕ Descartar y pedir otro</button>
              </div>
            </>
          )}
        </section>

        {/* CALENDARIO — cómo viene cada tema, día por día */}
        <section className="com-card">
          <div className="com-card-head">
            <div>
              <h2 className="com-card-title">📅 El calendario, día por día</h2>
              <span className="com-card-sub">Últimos 7 días + lo que viene. Cada tema dice quién lo tiene: vos, el Gerente o ya salió.</span>
            </div>
            <Link to="/admin/calendario-marketing" className="com-btn com-btn-ghost" style={{ textDecoration: 'none' }}>
              ➕ Cargar / editar a mano →
            </Link>
          </div>

          {loading && <div className="com-empty">Cargando…</div>}

          {!loading && porDia.length === 0 && (
            <div className="com-empty">
              <strong>Calendario vacío.</strong>
              No hay nada planificado. El plan lo arma el Estratega (botón de arriba) o cargás piezas a mano con "Cargar / editar a mano".
            </div>
          )}

          <div className="cplan-dias">
            {porDia.map(([fecha, items]) => {
              const esHoy = fecha === hoyISO
              const pasado = fecha < hoyISO
              return (
                <div key={fecha} className={`cplan-dia ${esHoy ? 'cplan-dia-hoy' : ''} ${pasado ? 'cplan-dia-pasado' : ''}`}>
                  <div className="cplan-dia-head">
                    <span className="cplan-dia-fecha">{fmtDia(fecha)}</span>
                    {esHoy && <span className="com-pill com-pill-teal">HOY</span>}
                  </div>
                  {items.map(i => {
                    const e = ESTADO_FILA[i.estado] || ESTADO_FILA.pendiente
                    const filaJsx = (
                      <>
                        <span className="cplan-fila-hora">{(i.hora_sugerida || '').slice(0, 5)}</span>
                        <span className="cplan-fila-canal">{CANAL_MINI[i.canal] || i.canal}</span>
                        <span className="cplan-fila-titulo">{i.titulo}</span>
                        <span className="cplan-fila-estado" style={{ background: e.bg, color: e.color }}>{e.txt}</span>
                      </>
                    )
                    // Las escritas linkean a Piezas: ahí está el texto + imagen real
                    return i.estado === 'en_proceso' ? (
                      <Link key={i.id} to="/admin/comunicacion/piezas" className="cplan-fila cplan-fila-link">{filaJsx}</Link>
                    ) : (
                      <div key={i.id} className="cplan-fila">{filaJsx}</div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </section>

      </div>

      <style>{COM_CSS}</style>
      <style>{FLUJO_CSS}</style>
      <style>{`
        .cplan-resumen {
          font-size: 12.5px; color: #374151; background: #FBF7EE; border: 1px solid #E9DFC8;
          border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; white-space: pre-wrap;
        }
        .cplan-resumen-eyebrow { font-size: 10.5px; font-weight: 800; color: #8a6d2f; letter-spacing: .06em; margin-bottom: 4px; }
        .cplan-fila {
          display: flex; align-items: center; gap: 10px; padding: 8px 10px;
          background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px; font-size: 12.5px;
          flex-wrap: wrap; text-decoration: none; color: inherit;
        }
        .cplan-fila-link:hover { border-color: #00A8B5; }
        .cplan-fila-fecha { font-weight: 800; color: #1A3461; min-width: 58px; }
        .cplan-fila-hora { color: #6B7280; min-width: 40px; }
        .cplan-fila-canal { min-width: 68px; color: #00666B; font-weight: 700; }
        .cplan-fila-titulo { flex: 1; min-width: 140px; color: #111827; }
        .cplan-fila-estado {
          font-size: 10px; font-weight: 800; padding: 3px 9px; border-radius: 999px; white-space: nowrap;
        }
        .cplan-dias { display: flex; flex-direction: column; gap: 12px; }
        .cplan-dia { display: flex; flex-direction: column; gap: 6px; }
        .cplan-dia-pasado { opacity: .65; }
        .cplan-dia-hoy .cplan-fila { border-color: #9bdfe4; }
        .cplan-dia-head { display: flex; align-items: center; gap: 8px; }
        .cplan-dia-fecha { font-size: 12px; font-weight: 800; color: #1A3461; text-transform: capitalize; }
        @media (max-width: 480px) {
          .cplan-fila-estado { width: 100%; text-align: left; }
        }
      `}</style>
    </AdminLayout>
  )
}

function fmtF(fecha) {
  const f = new Date(fecha + 'T12:00:00-03:00')
  return `${['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'][f.getDay()]} ${String(f.getDate()).padStart(2, '0')}`
}
